// ─── v1.3 UI: profile tab (accounts + badges), location prompt + bar ──────
import React, { useState } from "react";
import { T, SANS } from "./components";
import {
  IconPin, IconRefresh, IconTrophy,
  IconMoon, IconSpark, IconCheck, IconBookmark, IconBag, IconFlame,
} from "./icons";
import { AUTH_READY } from "./auth";
import { CountUp } from "./play";
import {
  SUPABASE_READY, KEYS_FROM_APP, getSavedConnKeys, saveConnKeys,
} from "./supabase";

// Early Adopter = signed up while the app is in beta.
const EARLY_ADOPTER_CUTOFF = "2027-06-01T00:00:00Z";

// ─── v3.0 badges: local-first, demo-mode friendly ─────────────────────────
// Progress comes from funStats (localStorage: saves, streak, night-owl),
// so badges render and progress with or without sign-in. Playful
// descriptions, visible progress — never loud.
export const BADGES = [
  {
    id: "first-dibs", name: "First Dibs",
    desc: "Saved your first deal. The fridge thanks you.",
    Icon: IconBookmark, kind: "count", target: 1, unit: "save",
    value: (s) => s.saves || 0,
  },
  {
    id: "stockpiler", name: "Stockpiler",
    desc: "10 deals on the list. The flyers fear you.",
    Icon: IconBag, kind: "count", target: 10, unit: "save",
    value: (s) => s.saves || 0,
  },
  {
    id: "neighbourhood-hero", name: "Neighbourhood Hero",
    desc: "25 saves. Certified bargain legend.",
    Icon: IconTrophy, kind: "count", target: 25, unit: "save",
    value: (s) => s.saves || 0,
  },
  {
    id: "night-owl", name: "Night Owl",
    desc: "Browsed deals after 8pm. Respect.",
    Icon: IconMoon, kind: "bool", hint: "Open the app after 8pm",
    value: (s) => !!s.nightOwl,
  },
  {
    id: "on-a-roll", name: "On a Roll",
    desc: "A 3-day deal-checking streak. Momentum!",
    Icon: IconFlame, kind: "count", target: 3, unit: "day",
    value: (s) => s.streak || 0,
  },
  {
    id: "early-adopter", name: "Early Adopter",
    desc: "Here before it was cool.",
    Icon: IconSpark, kind: "bool", hint: "Sign in to claim",
    value: (s) => !!s.earlyAdopter,
  },
];

// ─── Badge shelf ─────────────────────────────────────────────────────────
export function BadgeShelf({ stats }) {
  const s = stats || {};
  return (
    <div>
      <div style={styles.sectionTitle}>Badges</div>
      <div style={styles.badgeGrid}>
        {BADGES.map((b) => {
          const v = b.value(s);
          const has = b.kind === "bool" ? !!v : v >= b.target;
          const Icon = b.Icon;
          const remaining = b.kind === "count" ? Math.max(0, b.target - v) : 0;
          return (
            <div key={b.id} style={styles.badgeCell}>
              <div style={{ ...styles.medallion, ...(has ? styles.medallionOn : styles.medallionOff) }}>
                <Icon size={24} />
                {has && <span style={styles.medallionTick}><IconCheck size={11} /></span>}
              </div>
              <div style={{ ...styles.badgeName, color: has ? T.text : T.textMuted }}>{b.name}</div>
              <div style={styles.badgeDesc}>{b.desc}</div>
              <div style={styles.badgeProg} className="tnum">
                {has
                  ? "Unlocked"
                  : b.kind === "count"
                    ? `${remaining} more ${b.unit}${remaining === 1 ? "" : "s"} to go`
                    : b.hint}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── v3.0: "This week" score card ────────────────────────────────────────
// "You've saved $X this week" — playful count-up, honest number (only
// deals with a listed was-price count). Renders in demo mode too.
function FunCard({ stats }) {
  const s = stats || {};
  return (
    <div style={{ ...styles.card, ...styles.funCard }}>
      <div style={styles.funEyebrow}>Your week in savings</div>
      <div style={styles.funScore}>
        <CountUp
          value={s.savedTotal || 0}
          format={(v) => "$" + v.toFixed(2).replace(/\.00$/, "")}
        />
      </div>
      <div style={styles.funLabel}>saved from your list — nice hunting.</div>
      <div style={styles.funMeta}>
        <span className="tnum">{s.listCount || 0} on your list</span>
        {(s.streak || 0) > 1 && (
          <span style={styles.funStreak}>
            <IconFlame size={13} filled />
            <span className="tnum">{s.streak}-day streak</span>
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Magic-link sign-in form ─────────────────────────────────────────────
function SignInForm({ onSendLink }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [err, setErr] = useState("");

  async function submit() {
    const clean = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      setErr("Enter a valid email address.");
      return;
    }
    setState("sending");
    setErr("");
    try {
      await onSendLink(clean);
      setState("sent");
    } catch (e) {
      setState("error");
      setErr((e && e.message) || "Couldn't send the link — try again.");
    }
  }

  if (state === "sent") {
    return (
      <div style={styles.card}>
        <div style={styles.sentIcon}><IconCheck size={22} /></div>
        <div style={styles.cardTitle}>Check your email</div>
        <div style={styles.cardText}>
          Tap the sign-in link we just sent to <b>{email.trim()}</b>. It expires
          in an hour — no password to remember.
        </div>
        <button onClick={() => setState("idle")} style={styles.ghostBtn}>
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Sign in to earn badges</div>
      <div style={styles.cardText}>
        Every “Still there?” tap earns you badges.
      </div>
      <label style={styles.label}>Email</label>
      <input
        style={styles.input}
        type="email"
        inputMode="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
      />
      {err && <div style={styles.error}>{err}</div>}
      <button onClick={submit} style={styles.submit} disabled={state === "sending"}>
        {state === "sending" ? "Sending…" : "Send sign-in link"}
      </button>
      <div style={styles.finePrint}>Passwordless — a magic link, nothing to remember.</div>
    </div>
  );
}

// ─── Profile tab ─────────────────────────────────────────────────────────
export function ProfileTab({
  user, authLoaded, profile, profileLoading,
  onSendLink, onSignOut, onSaveName, lbsRescued,
  locPref, locBusy, onLocate, funStats,
}) {
  // v3.0: badges + score are local-first — they render in demo mode too.
  const stats = {
    ...(funStats || {}),
    earlyAdopter: !!profile?.created_at && profile.created_at < EARLY_ADOPTER_CUTOFF,
  };
  let body;
  if (!AUTH_READY) {
    body = (
      <div style={styles.card}>
        <div style={styles.cardTitle}>Accounts are in demo mode</div>
        <div style={styles.cardText}>
          Paste your Supabase keys below to enable sign-in, badges and trust lines.
        </div>
      </div>
    );
  } else if (!authLoaded || (user && profileLoading)) {
    body = <div style={styles.loading}>Loading…</div>;
  } else if (!user) {
    body = <SignInForm onSendLink={onSendLink} />;
  } else {
    body = (
      <ProfileHome
        user={user} profile={profile}
        onSignOut={onSignOut} onSaveName={onSaveName} lbsRescued={lbsRescued}
        locPref={locPref} locBusy={locBusy} onLocate={onLocate}
      />
    );
  }
  return (
    <div>
      <FunCard stats={stats} />
      <div style={styles.card}>
        <BadgeShelf stats={stats} />
      </div>
      {body}
      <ConnectionCard />
    </div>
  );
}

// ─── Connection: Supabase keys saved in this browser ────────────────────
// Survives zip overwrites (which wipe src/supabaseConfig.js). The anon key
// is public-by-design, so localStorage is fine. Saving reloads the app so
// the Supabase client picks up the new keys.
function ConnectionCard() {
  const [url, setUrl] = useState(() => getSavedConnKeys().url);
  const [key, setKey] = useState(() => getSavedConnKeys().key);
  const [saving, setSaving] = useState(false);
  const live = SUPABASE_READY;

  function save() {
    saveConnKeys(url, key);
    setSaving(true);
    setTimeout(() => window.location.reload(), 700);
  }

  return (
    <div style={styles.card}>
      <div style={styles.sectionTitle}>Connection</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span
          style={{
            width: 9, height: 9, borderRadius: 9,
            background: live ? "#1F7A4D" : "#C9A227",
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>
          {live ? "Live sync on" : "Demo mode"}
        </span>
        {live && KEYS_FROM_APP && (
          <span style={{ fontSize: 12, color: T.textMuted }}>
            · keys saved in this browser
          </span>
        )}
      </div>
      <input
        type="password"
        style={{ ...styles.input, marginBottom: 8 }}
        placeholder="Supabase URL (https://…supabase.co)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        autoComplete="off"
        aria-label="Supabase URL"
      />
      <input
        type="password"
        style={{ ...styles.input, marginBottom: 12 }}
        placeholder="Anon public key"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        autoComplete="off"
        aria-label="Supabase anon key"
      />
      <button onClick={save} style={styles.smallBtn} disabled={saving}>
        {saving ? "Connecting…" : "Save"}
      </button>
      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 10, lineHeight: 1.5 }}>
        Paste once — keys stay in this browser through app updates.
        Never paste your service_role key.
      </div>
    </div>
  );
}

function ProfileHome({ user, profile, onSignOut, onSaveName, lbsRescued, locPref, locBusy, onLocate }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(profile ? profile.display_name : "");
  const [saving, setSaving] = useState(false);

  const displayName = (profile && profile.display_name) || "Neighbor";
  const alerts = (profile && profile.alert_count) || 0;
  const confirms = (profile && profile.confirm_count) || 0;

  async function save() {
    setSaving(true);
    try { await onSaveName(name); } finally { /* parent refreshes */ }
    setSaving(false);
    setEditing(false);
  }

  return (
    <div>
      <div style={styles.card}>
        <div style={styles.meRow}>
          <div style={styles.avatar}>{displayName.slice(0, 1).toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editing ? (
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  style={{ ...styles.input, margin: 0 }}
                  value={name}
                  maxLength={40}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                  autoFocus
                />
                <button onClick={save} style={styles.smallBtn} disabled={saving}>
                  {saving ? "…" : "Save"}
                </button>
              </div>
            ) : (
              <>
                <div style={styles.meName}>{displayName}</div>
                <button onClick={() => { setName(displayName); setEditing(true); }} style={styles.linkBtn}>
                  Edit name
                </button>
              </>
            )}
            <div style={styles.meEmail}>{user.email}</div>
          </div>
        </div>
        <div style={styles.statRow}>
          <div style={styles.stat}>
            <div className="tnum" style={styles.statNum}>{alerts}</div>
            <div style={styles.statLabel}>alerts posted</div>
          </div>
          <div style={styles.stat}>
            <div className="tnum" style={styles.statNum}>{confirms}</div>
            <div style={styles.statLabel}>confirmed</div>
          </div>
          <div style={styles.stat}>
            <div className="tnum" style={styles.statNum}>{Number(lbsRescued || 0).toFixed(1)}</div>
            <div style={styles.statLabel}>lbs rescued (est.)</div>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>Location</div>
        <div style={styles.locRow}>
          <span style={{ color: T.accentDark }}><IconPin size={18} /></span>
          <div style={{ flex: 1 }}>
            <div style={styles.locText}>
              {locPref.status === "granted"
                ? "On — deals sort nearest-first."
                : "Off — deals show in flyer order."}
            </div>
          </div>
          <button onClick={onLocate} style={styles.smallBtn} disabled={locBusy}>
            {locBusy ? "…" : locPref.status === "granted" ? "Refresh" : "Turn on"}
          </button>
        </div>
      </div>

      <button onClick={onSignOut} style={styles.signOut}>Sign out</button>
    </div>
  );
}

// ─── First-launch location prompt (home feed) ────────────────────────────
export function LocationPrompt({ onAllow, onDismiss, busy }) {
  return (
    <div style={styles.prompt}>
      <div style={styles.promptIcon}><IconPin size={26} /></div>
      <div style={styles.promptTitle}>Find deals near me</div>
      <div style={styles.promptText}>
        One tap — we'll sort flyer deals by nearest store first.
      </div>
      <button onClick={onAllow} style={styles.submit} disabled={busy}>
        {busy ? "Locating…" : "Use my location"}
      </button>
      <button onClick={onDismiss} style={styles.dismissBtn} disabled={busy}>
        Not now
      </button>
    </div>
  );
}

// ─── Slim location bar (when location is on) ────────────────────────────
export function LocationBar({ onRefresh, refreshing }) {
  return (
    <div style={styles.locBar}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.accentDark }}>
        <IconPin size={14} />
        <span style={{ fontSize: 13, fontWeight: 700 }}>Near you · nearest first</span>
      </span>
      <button
        onClick={onRefresh}
        style={styles.barRefresh}
        aria-label="Refresh location"
        disabled={refreshing}
      >
        <IconRefresh size={14} /> <span style={{ fontSize: 12.5, fontWeight: 700 }}>Refresh</span>
      </button>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────
const styles = {
  card: {
    background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 18,
    padding: 18, boxShadow: T.shadow, marginBottom: 12,
  },
  loading: { textAlign: "center", color: T.textMuted, padding: 30, fontSize: 14 },
  cardTitle: { fontSize: 17, fontWeight: 700, marginBottom: 6 },
  cardText: { fontSize: 13.5, color: T.textSub, lineHeight: 1.55, marginBottom: 6 },
  label: { fontSize: 11, textTransform: "uppercase", letterSpacing: 1.4, color: T.textMuted, margin: "14px 0 6px", display: "block", fontWeight: 600 },
  input: {
    width: "100%", padding: 13, background: T.bgCardAlt, border: `1.5px solid ${T.border}`,
    borderRadius: 12, color: T.text, fontSize: 16, outline: "none", boxSizing: "border-box", fontFamily: SANS,
  },
  submit: {
    width: "100%", padding: 16, borderRadius: 999, border: "none", background: "#141210",
    color: "#fff", fontSize: 15.5, fontWeight: 800, letterSpacing: -0.2, cursor: "pointer", marginTop: 14, fontFamily: SANS,
  },
  ghostBtn: {
    width: "100%", padding: 13, borderRadius: 12, border: `1.5px solid ${T.border}`,
    background: "transparent", color: T.textSub, fontSize: 14, fontWeight: 600,
    cursor: "pointer", marginTop: 12, fontFamily: SANS,
  },
  smallBtn: {
    padding: "9px 14px", borderRadius: 11, border: "none", background: T.accent,
    color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer",
    fontFamily: SANS, whiteSpace: "nowrap",
  },
  linkBtn: {
    background: "none", border: "none", padding: 0, color: T.accentDark,
    fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, marginTop: 2,
  },
  error: { background: T.redBg, color: T.red, borderRadius: 10, padding: "10px 13px", fontSize: 13.5, marginTop: 12, fontWeight: 600 },
  finePrint: { fontSize: 11.5, color: T.textMuted, textAlign: "center", marginTop: 10, lineHeight: 1.5 },
  sentIcon: {
    width: 46, height: 46, borderRadius: 23, background: T.accentLight,
    color: T.accentDark, display: "flex", alignItems: "center", justifyContent: "center",
    marginBottom: 12,
  },
  meRow: { display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16 },
  avatar: {
    width: 56, height: 56, borderRadius: 28, background: T.accentDark, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 24, fontWeight: 700, fontFamily: SANS, flexShrink: 0,
  },
  meName: { fontSize: 18, fontWeight: 700 },
  meEmail: { fontSize: 12.5, color: T.textMuted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis" },
  statRow: { display: "flex", borderTop: `1px solid ${T.border}`, paddingTop: 14 },
  stat: { flex: 1, textAlign: "center" },
  statNum: { fontSize: 26, fontWeight: 800, letterSpacing: -0.5, color: T.text },
  statLabel: { fontSize: 11, color: T.textMuted, marginTop: 2 },
  sectionTitle: { fontSize: 11, textTransform: "uppercase", letterSpacing: 1.4, color: T.textMuted, fontWeight: 700, marginBottom: 12 },
  badgeGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px 8px" },
  badgeCell: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" },
  medallion: {
    width: 58, height: 58, borderRadius: 29, display: "flex",
    alignItems: "center", justifyContent: "center", position: "relative", marginBottom: 7,
  },
  medallionOn: {
    background: "linear-gradient(145deg, #F6D876 0%, #D9A62E 60%, #B98A1D 100%)",
    color: "#4A3405", boxShadow: "0 3px 10px rgba(185,138,29,0.35), inset 0 1px 2px rgba(255,255,255,0.5)",
  },
  medallionOff: { background: T.bgCardAlt, border: `1.5px solid ${T.border}`, color: T.textMuted, opacity: 0.55 },
  medallionTick: {
    position: "absolute", right: -2, bottom: -2, width: 20, height: 20, borderRadius: 10,
    background: T.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    border: "2px solid #fff",
  },
  badgeName: { fontSize: 12.5, fontWeight: 700 },
  badgeDesc: { fontSize: 10.5, color: T.textMuted, marginTop: 2, lineHeight: 1.35 },
  // v3.0: visible progress toward each badge — "7 more saves to go".
  badgeProg: { fontSize: 10.5, color: T.accentDark, fontWeight: 700, marginTop: 3 },
  // v3.0: "This week" score card.
  funCard: {
    background: "#141210", border: "none", color: "#fff",
    overflow: "hidden", position: "relative",
  },
  funEyebrow: {
    fontSize: 10.5, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: 2.2, color: "rgba(255,255,255,0.6)", marginBottom: 8,
  },
  funScore: {
    fontSize: 44, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1,
    color: "#fff",
  },
  funLabel: { fontSize: 13.5, color: "rgba(255,255,255,0.75)", marginTop: 6, fontWeight: 600 },
  funMeta: {
    display: "flex", alignItems: "center", gap: 12, marginTop: 12,
    fontSize: 12.5, color: "rgba(255,255,255,0.6)", fontWeight: 600,
  },
  funStreak: {
    display: "inline-flex", alignItems: "center", gap: 4,
    color: "#FFC46B",
  },
  locRow: { display: "flex", alignItems: "center", gap: 10 },
  locText: { fontSize: 13.5, color: T.textSub },
  signOut: {
    width: "100%", padding: 13, borderRadius: 12, border: `1.5px solid ${T.border}`,
    background: "transparent", color: T.textSub, fontSize: 14, fontWeight: 600,
    cursor: "pointer", fontFamily: SANS,
  },
  prompt: {
    background: T.bgCard, border: `1.5px solid ${T.accentBorder}`, borderRadius: 20,
    padding: "22px 20px 16px", boxShadow: T.shadow, marginBottom: 14, textAlign: "center",
  },
  promptIcon: {
    width: 52, height: 52, borderRadius: 16, background: T.accentLight, color: T.accentDark,
    display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 10,
  },
  promptTitle: { fontSize: 18, fontWeight: 700, marginBottom: 5 },
  promptText: { fontSize: 13.5, color: T.textSub, lineHeight: 1.5, marginBottom: 4 },
  dismissBtn: {
    background: "none", border: "none", color: T.textMuted, fontSize: 13.5,
    fontWeight: 600, cursor: "pointer", marginTop: 10, padding: "8px 16px", fontFamily: SANS,
  },
  locBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: T.bgCard, border: `1px solid ${T.accentBorder}`, borderRadius: 14,
    padding: "10px 14px", marginBottom: 12,
  },
  barRefresh: {
    display: "inline-flex", alignItems: "center", gap: 5, background: "none",
    border: "none", color: T.textSub, cursor: "pointer", fontFamily: SANS, padding: "6px 4px",
  },
};
